// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BrandWallet.jsx
// Название: Brand Wallet - Business Financial Dashboard
// Версия: 3.0.0
// Дата: 27.01.2026

import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

const BrandWallet = () => {
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [topupAmount, setTopupAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAllTx, setShowAllTx] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [walletRes, transRes] = await Promise.all([
        api.get('/brand-wallet').catch(() => null),
        api.get('/brand-wallet/transactions?limit=100').catch(() => null)
      ]);

      if (walletRes) {
        setWallet(walletRes.wallet || walletRes.data?.wallet);
      }
      if (transRes) {
        setTransactions(transRes.transactions || transRes.data?.transactions || []);
      }
    } catch (err) {
      console.error('Load wallet error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    const amount = customAmount ? Number(customAmount) : topupAmount;
    
    if (amount < 10 || amount > 10000) {
      showMessage('error', 'Сумма должна быть от €10 до €10 000');
      return;
    }

    try {
      setProcessing(true);
      const res = await api.post('/billing/create-checkout', { amount_eur: amount });
      
      if (res.provider === 'mock') {
        await api.post('/billing/mock-confirm', { session_id: res.session_id });
        await loadData();
        setCustomAmount('');
        showMessage('success', `✅ Баланс пополнен на €${amount}`);
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err) {
      showMessage('error', err.message || 'Ошибка пополнения');
    } finally {
      setProcessing(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    if (type === 'success') setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatAmount = (val) => {
    return parseFloat(val || 0).toLocaleString('ru-RU', { 
      minimumFractionDigits: 2, maximumFractionDigits: 2 
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getTypeInfo = (type) => {
    const map = {
      deposit: { label: 'Пополнение', icon: '💰', color: '#10B981' },
      reserve: { label: 'Резерв', icon: '📦', color: '#F59E0B' },
      lead_payment: { label: 'Оплата лида', icon: '🎯', color: '#8B5CF6' },
      lead_activated: { label: 'Лид активирован', icon: '✅', color: '#10B981' },
      refund: { label: 'Возврат', icon: '↩️', color: '#3B82F6' },
      subscription: { label: 'Подписка', icon: '💎', color: '#EC4899' },
      release: { label: 'Разблокировка', icon: '🔓', color: '#6B7280' }
    };
    return map[type] || { label: type || 'Операция', icon: '📄', color: '#6B7280' };
  };

  const totalReserved = parseFloat(wallet?.fees_reserve || 0);
  const usedPercent = wallet?.deposit_balance > 0 
    ? (totalReserved / wallet.deposit_balance * 100).toFixed(0) 
    : 0;
  const presetAmounts = [50, 100, 250, 500, 1000];
  const currentAmount = customAmount ? Number(customAmount) : topupAmount;

  const filteredTx = activeFilter === 'all' 
    ? transactions 
    : transactions.filter(t => {
        if (activeFilter === 'deposit') return t.type === 'deposit';
        if (activeFilter === 'reserve') return t.type === 'reserve' || t.type === 'release';
        if (activeFilter === 'lead') return t.type === 'lead_payment' || t.type === 'lead_activated';
        if (activeFilter === 'refund') return t.type === 'refund';
        return true;
      });

  const displayedTx = showAllTx ? filteredTx : filteredTx.slice(0, 10);

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loading}>⏳ Загрузка кошелька...</div>
      </div>
    );
  }

  if (!wallet) {
    return (
      <div style={s.page}>
        <div style={s.loading}>❌ Ошибка загрузки кошелька</div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>💰 Кошелёк</h1>
          <p style={s.subtitle}>Управление балансом для кампаний</p>
        </div>
        <button style={s.refreshBtn} onClick={loadData}>🔄 Обновить</button>
      </div>

      {/* Messages */}
      {message.text && (
        <div style={{...s.message, ...(message.type === 'success' ? s.msgSuccess : s.msgError)}}>
          {message.text}
        </div>
      )}

      {/* Balance Cards */}
      <div style={s.balanceGrid}>
        <div style={{...s.balanceCard, ...s.cardPrimary}}>
          <div style={s.cardIcon}>💰</div>
          <div>
            <div style={s.cardLabel}>Общий депозит</div>
            <div style={s.cardValue}>€{formatAmount(wallet.deposit_balance)}</div>
            <div style={s.cardSub}>Всего внесено: €{formatAmount(wallet.total_deposited)}</div>
          </div>
        </div>

        <div style={{...s.balanceCard, ...s.cardGreen}}>
          <div style={s.cardIcon}>✅</div>
          <div>
            <div style={s.cardLabel}>Доступно</div>
            <div style={{...s.cardValue, color: '#059669'}}>€{formatAmount(wallet.available_balance)}</div>
            <div style={s.cardSub}>Для новых кампаний</div>
          </div>
        </div>

        <div style={{...s.balanceCard, ...s.cardOrange}}>
          <div style={s.cardIcon}>📦</div>
          <div>
            <div style={s.cardLabel}>В кампаниях</div>
            <div style={{...s.cardValue, color: '#d97706'}}>€{formatAmount(totalReserved)}</div>
            <div style={s.cardSub}>Зарезервировано</div>
          </div>
        </div>

        <div style={{...s.balanceCard, ...s.cardBlue}}>
          <div style={s.cardIcon}>🎯</div>
          <div>
            <div style={s.cardLabel}>Оплачено лидов</div>
            <div style={{...s.cardValue, color: '#2563eb'}}>€{formatAmount(wallet.total_fees_paid)}</div>
            <div style={s.cardSub}>Всего за лидов</div>
          </div>
        </div>
      </div>

      <div style={s.mainGrid}>
        {/* Left: Usage + Topup */}
        <div>
          {/* Usage Progress */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>📊 Использование баланса</h3>
            
            <div style={s.progressHeader}>
              <span>Зарезервировано: €{formatAmount(totalReserved)}</span>
              <span style={{fontWeight: 700}}>{usedPercent}%</span>
            </div>
            <div style={s.progressBar}>
              <div style={{
                ...s.progressFill,
                width: `${Math.min(usedPercent, 100)}%`,
                background: usedPercent > 80 ? '#ef4444' : usedPercent > 50 ? '#f59e0b' : '#10b981'
              }} />
            </div>

            <div style={s.statsGrid}>
              <div style={s.statBox}>
                <div style={{...s.statValue, color: '#8B5CF6'}}>€{formatAmount(wallet.total_fees_paid)}</div>
                <div style={s.statLabel}>Оплачено лидов</div>
              </div>
              <div style={s.statBox}>
                <div style={s.statValue}>{transactions.length}</div>
                <div style={s.statLabel}>Транзакций</div>
              </div>
            </div>
          </div>

          {/* Topup */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>➕ Пополнить баланс</h3>
            
            <div style={s.presets}>
              {presetAmounts.map(amt => (
                <button
                  key={amt}
                  style={{
                    ...s.presetBtn,
                    ...(topupAmount === amt && !customAmount ? s.presetActive : {})
                  }}
                  onClick={() => { setTopupAmount(amt); setCustomAmount(''); }}
                >
                  €{amt}
                </button>
              ))}
            </div>

            <div style={s.customInput}>
              <label style={s.inputLabel}>Или введите сумму:</label>
              <div style={s.inputRow}>
                <input
                  type="number"
                  style={s.input}
                  placeholder="10 - 10 000"
                  value={customAmount}
                  onChange={e => setCustomAmount(e.target.value)}
                  min="10"
                  max="10000"
                />
                <span style={s.inputSuffix}>€</span>
              </div>
            </div>

            <div style={s.summary}>
              <div style={s.summaryRow}>
                <span>Вы платите:</span>
                <span style={{fontWeight: 700}}>{currentAmount || 0} €</span>
              </div>
              <div style={s.summaryRow}>
                <span>Получите:</span>
                <span style={{fontWeight: 800, color: '#10B981'}}>{currentAmount || 0} € на баланс</span>
              </div>
            </div>

            <button
              style={{...s.topupBtn, ...(processing || currentAmount < 10 ? s.btnDisabled : {})}}
              onClick={handleTopup}
              disabled={processing || currentAmount < 10}
            >
              {processing ? '⏳ Обработка...' : `💳 Пополнить €${currentAmount || 0}`}
            </button>

            <div style={s.notes}>
              <p>💡 <strong>€20 за лида</strong> — фиксированная стоимость активированного клиента</p>
              <p>↩️ Неиспользованные средства возвращаются при завершении кампании</p>
            </div>
          </div>
        </div>

        {/* Right: Transactions */}
        <div style={s.card}>
          <div style={s.txHeader}>
            <h3 style={s.cardTitle}>📋 История операций</h3>
            <span style={s.txCount}>{filteredTx.length} операций</span>
          </div>

          <div style={s.filters}>
            {[
              { id: 'all', label: 'Все' },
              { id: 'deposit', label: '💰 Пополнения' },
              { id: 'lead', label: '🎯 Лиды' },
              { id: 'reserve', label: '📦 Резервы' },
              { id: 'refund', label: '↩️ Возвраты' }
            ].map(f => (
              <button
                key={f.id}
                style={{...s.filterBtn, ...(activeFilter === f.id ? s.filterActive : {})}}
                onClick={() => setActiveFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {filteredTx.length === 0 ? (
            <div style={s.empty}>
              <div style={{fontSize: 48, marginBottom: 12}}>📭</div>
              <p>Нет операций</p>
            </div>
          ) : (
            <>
              <div style={s.txList}>
                {displayedTx.map(tx => {
                  const info = getTypeInfo(tx.type);
                  const amount = parseFloat(tx.amount);
                  const isPositive = amount >= 0;

                  return (
                    <div key={tx.id} style={s.txItem}>
                      <div style={s.txLeft}>
                        <div style={{...s.txIcon, background: isPositive ? '#dcfce7' : '#fee2e2'}}>
                          {info.icon}
                        </div>
                        <div>
                          <div style={s.txDesc}>{tx.description || info.label}</div>
                          <div style={s.txDate}>{formatDate(tx.created_at)}</div>
                        </div>
                      </div>
                      <div style={{...s.txAmount, color: isPositive ? '#059669' : '#dc2626'}}>
                        {isPositive ? '+' : ''}€{formatAmount(Math.abs(amount))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {filteredTx.length > 10 && (
                <button 
                  style={s.showMoreBtn}
                  onClick={() => setShowAllTx(!showAllTx)}
                >
                  {showAllTx ? '↑ Свернуть' : `↓ Показать все (${filteredTx.length})`}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Styles
const s = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  loading: { textAlign: 'center', padding: 60, fontSize: 18, color: '#6b7280' },
  
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, color: '#1f2937', marginBottom: 4 },
  subtitle: { fontSize: 15, color: '#6b7280', margin: 0 },
  refreshBtn: { padding: '10px 20px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 14 },

  message: { padding: '12px 16px', borderRadius: 10, marginBottom: 20, fontSize: 14, fontWeight: 500 },
  msgSuccess: { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
  msgError: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },

  // Balance Cards
  balanceGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  balanceCard: { display: 'flex', alignItems: 'center', gap: 16, padding: 20, borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
  cardPrimary: { background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: 'white' },
  cardGreen: { background: '#f0fdf4', border: '2px solid #86efac' },
  cardOrange: { background: '#fffbeb', border: '2px solid #fcd34d' },
  cardBlue: { background: '#eff6ff', border: '2px solid #93c5fd' },
  cardIcon: { fontSize: 32 },
  cardLabel: { fontSize: 12, opacity: 0.8, marginBottom: 4 },
  cardValue: { fontSize: 24, fontWeight: 800 },
  cardSub: { fontSize: 11, opacity: 0.7, marginTop: 4 },

  // Main Grid
  mainGrid: { display: 'grid', gridTemplateColumns: '380px 1fr', gap: 24 },
  card: { background: 'white', borderRadius: 20, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: 20 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: '#1f2937', margin: '0 0 20px 0' },

  // Progress
  progressHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, color: '#4b5563' },
  progressBar: { height: 10, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden', marginBottom: 20 },
  progressFill: { height: '100%', borderRadius: 5, transition: 'width 0.3s' },

  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  statBox: { textAlign: 'center', padding: 16, background: '#f9fafb', borderRadius: 12 },
  statValue: { fontSize: 22, fontWeight: 800, marginBottom: 4 },
  statLabel: { fontSize: 12, color: '#6b7280' },

  // Topup
  presets: { display: 'flex', gap: 8, marginBottom: 16 },
  presetBtn: { flex: 1, padding: '12px 8px', background: '#f9fafb', border: '2px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#374151' },
  presetActive: { borderColor: '#10B981', background: '#f0fdf4', color: '#059669' },
  
  customInput: { marginBottom: 16 },
  inputLabel: { fontSize: 13, color: '#6b7280', marginBottom: 8, display: 'block' },
  inputRow: { display: 'flex', alignItems: 'center', gap: 8 },
  input: { flex: 1, padding: '14px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 16, fontWeight: 600, outline: 'none' },
  inputSuffix: { fontSize: 18, fontWeight: 700, color: '#6b7280' },

  summary: { background: '#f0fdf4', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid #86efac' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, color: '#4b5563' },

  topupBtn: { width: '100%', padding: '16px 20px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 16 },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },

  notes: { marginTop: 16, padding: 16, background: '#f9fafb', borderRadius: 12, fontSize: 13, color: '#4b5563', lineHeight: 1.6 },

  // Transactions
  txHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  txCount: { fontSize: 13, color: '#9ca3af' },
  filters: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  filterBtn: { padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#6b7280' },
  filterActive: { background: '#8B5CF6', color: 'white' },

  empty: { textAlign: 'center', padding: 40, color: '#9ca3af' },
  txList: {},
  txItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid #f3f4f6' },
  txLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  txIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 },
  txDesc: { fontSize: 14, fontWeight: 500, color: '#1f2937' },
  txDate: { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  txAmount: { fontSize: 16, fontWeight: 700 },
  
  showMoreBtn: { 
    width: '100%', 
    padding: '12px', 
    background: '#f9fafb', 
    border: '1px solid #e5e7eb', 
    borderRadius: 10, 
    cursor: 'pointer', 
    fontSize: 14, 
    fontWeight: 600, 
    color: '#6b7280',
    marginTop: 16
  }
};

export default BrandWallet;